import { showToast } from "../Utility";
import * as Cesium from "cesium";

export const getEditedGeoJSON = (item) => {
  let dataSource = item.dataSource;
  let oldGeojson = item.original_geojson;

  if (
    item.file_name
      .toLowerCase()
      .replace(/[\s\-_]/g, "")
      .includes("overlaytext")
  ) {
    return oldGeojson;
  }

  const notify = (msg, type = "error") => {
    if (typeof showToast === "function") {
      try {
        showToast(msg, type);
        return;
      } catch {}
    }
    console.warn(`[getEditedGeoJSON] ${msg}`);
  };

  if (!dataSource?.entities?.values || !Array.isArray(dataSource.entities.values)) {
    notify("Invalid dataSource or entities missing. Returning a safe FeatureCollection.");
    const safe = { type: "FeatureCollection", features: [] };
    if (oldGeojson && typeof oldGeojson === "object") {
      for (const key in oldGeojson) {
        if (Object.prototype.hasOwnProperty.call(oldGeojson, key)) {
          if (key !== "type" && key !== "features") safe[key] = oldGeojson[key];
        }
      }
    }
    return safe;
  }

  const time = Cesium.JulianDate.now();

  // round number
  const truncateNum = (num, digits) => {
    const n = Number(num);
    if (!Number.isFinite(n)) return NaN;
    const f = Math.pow(10, digits);
    return Math.round(n * f) / f;
  };

  // round coords and clamp negative height to 0
  const truncateCoord = (lon, lat, height) => {
    const lonR = truncateNum(lon, 7);
    const latR = truncateNum(lat, 7);
    // clamp negative heights to 0, treat non-finite as 0
    const hVal = height == null ? 0 : typeof height === "number" && Number.isFinite(height) ? Math.max(0, height) : 0;
    const hR = truncateNum(hVal, 2);
    if (!Number.isFinite(lonR) || !Number.isFinite(latR) || !Number.isFinite(hR)) return null;
    return [lonR, latR, hR];
  };

  const safeGet = (maybeProperty) => {
    try {
      if (!maybeProperty) return undefined;
      if (typeof maybeProperty.getValue === "function") return maybeProperty.getValue(time);
      return maybeProperty;
    } catch (err) {
      notify(`Failed to read Cesium property: ${err?.message || err}`);
      return undefined;
    }
  };

  // Detect id-like keys: "id" alone or "<word>_[iI][dD]" or "<word>-id"
  const isIdLikeKey = (key) => {
    if (typeof key !== "string") return false;
    return /^id$/i.test(key) || /(?:^|[_-])id$/i.test(key);
  };

  // Coerce numeric string to number (safe integer only)
  const coerceIdValue = (val) => {
    if (typeof val === "number" && Number.isFinite(val)) return val;
    if (typeof val === "string") {
      const s = val.trim();
      if (/^[+-]?\d+$/.test(s)) {
        const n = Number(s);
        if (Number.isSafeInteger(n)) return n;
      }
    }
    return val;
  };

  // Normalize top-level id-like props to numbers
  const normalizeIdLikeProps = (props) => {
    if (!props || typeof props !== "object") return props;
    for (const k of Object.keys(props)) {
      if (isIdLikeKey(k)) {
        const v = props[k];
        const coerced = coerceIdValue(v);
        props[k] = coerced;
      }
    }
    return props;
  };

  // Cartesian3 -> lon/lat/h
  const roundPositions = (positions) => {
    if (!positions || !positions.length) return [];
    const out = [];
    for (const p of positions) {
      try {
        if (!p) continue;
        const carto = Cesium.Cartographic.fromCartesian(p);
        const coord = truncateCoord(Cesium.Math.toDegrees(carto.longitude), Cesium.Math.toDegrees(carto.latitude), carto.height);
        if (coord) out.push(coord);
      } catch (err) {
        notify(`Skipped an invalid position: ${err?.message || err}`, "warning");
      }
    }
    return out;
  };

  // base collection
  const geojson = { type: "FeatureCollection", features: [] };
  if (oldGeojson && typeof oldGeojson === "object") {
    for (const key in oldGeojson) {
      if (Object.prototype.hasOwnProperty.call(oldGeojson, key)) {
        if (key !== "type" && key !== "features") geojson[key] = oldGeojson[key];
      }
    }
  }

  const entities = dataSource.entities.values;

  // build features
  for (const entity of entities) {
    try {
      const rawProps = entity?.properties ? safeGet(entity.properties) : {};
      const props = rawProps && typeof rawProps === "object" ? { ...rawProps } : {};

      // skip markers/owners
      if (props && (props._isHighlightVertexMarker !== undefined || props._ownerEntityId !== undefined)) {
        continue;
      }

      // drop volatile id if present
      if ("feature_id" in props) delete props.feature_id;

      // ensure id-like keys are numbers
      normalizeIdLikeProps(props);

      if (entity?.polyline) {
        const positions = safeGet(entity.polyline.positions);
        const coordinates = roundPositions(positions);
        if (coordinates.length >= 2) {
          geojson.features.push({
            type: "Feature",
            properties: props,
            geometry: { type: "LineString", coordinates },
          });
        } else if (coordinates.length > 0) {
          notify("Polyline has less than 2 valid positions. Skipped.", "warning");
        }
      } else if (entity?.polygon) {
        const hierarchy = safeGet(entity.polygon.hierarchy);
        const outer = hierarchy?.positions ? roundPositions(hierarchy.positions) : [];
        if (outer.length >= 3) {
          const holes = Array.isArray(hierarchy?.holes) ? hierarchy.holes : [];
          const holeCoordinates = holes.map((hole) => (hole?.positions ? roundPositions(hole.positions) : [])).filter((ring) => ring.length >= 3);

          geojson.features.push({
            type: "Feature",
            properties: props,
            geometry: { type: "Polygon", coordinates: [outer, ...holeCoordinates] },
          });
        } else if (outer.length > 0) {
          notify("Polygon outer ring has less than 3 valid positions. Skipped.", "warning");
        }
      } else if (entity?.point) {
        const position = safeGet(entity.position);
        if (position) {
          try {
            const carto = Cesium.Cartographic.fromCartesian(position);
            const coord = truncateCoord(Cesium.Math.toDegrees(carto.longitude), Cesium.Math.toDegrees(carto.latitude), carto.height);
            if (coord) {
              geojson.features.push({
                type: "Feature",
                properties: props,
                geometry: { type: "Point", coordinates: coord },
              });
            }
          } catch (err) {
            notify(`Invalid point position: ${err?.message || err}`, "warning");
          }
        }
      } else if (entity?.multiPoint) {
        const positions = safeGet(entity.multiPoint.positions);
        const coordinates = roundPositions(positions);
        if (coordinates.length > 0) {
          geojson.features.push({
            type: "Feature",
            properties: props,
            geometry: { type: "MultiPoint", coordinates },
          });
        }
      } else if (entity?.multiPolyline) {
        const lines = safeGet(entity.multiPolyline.positionsArray);
        const coordinates = Array.isArray(lines) ? lines.map((positions) => roundPositions(positions)).filter((line) => line.length >= 2) : [];
        if (coordinates.length > 0) {
          geojson.features.push({
            type: "Feature",
            properties: props,
            geometry: { type: "MultiLineString", coordinates },
          });
        }
      } else if (entity?.multiPolygon) {
        const polys = safeGet(entity.multiPolygon.hierarchyArray);
        const coordinates = Array.isArray(polys)
          ? polys
              .map((hierarchy) => {
                const outer = hierarchy?.positions ? roundPositions(hierarchy.positions) : [];
                if (outer.length < 3) return null;
                const holes = Array.isArray(hierarchy?.holes) ? hierarchy.holes : [];
                const holeCoords = holes.map((h) => (h?.positions ? roundPositions(h.positions) : [])).filter((ring) => ring.length >= 3);
                return [outer, ...holeCoords];
              })
              .filter(Boolean)
          : [];
        if (coordinates.length > 0) {
          geojson.features.push({
            type: "Feature",
            properties: props,
            geometry: { type: "MultiPolygon", coordinates },
          });
        }
      }
    } catch (err) {
      notify(`Failed to process an entity: ${err?.message || err}`);
    }
  }

  const hiddenFeatures = Array.isArray(item?.hiddenGeojson?.features) ? item.hiddenGeojson.features : [];

  console.log(item);
  if (hiddenFeatures.length > 0) {
    geojson.features = [...geojson.features, ...hiddenFeatures];
  }

  return geojson;
};
